/**
 * RecipeGraph — the drawing of a mapping recipe: entity cards joined by the links the
 * projection will create. Summary mode shows counts; row-lens mode shows one row's
 * values and the link it lands in. React Flow + dagre, tokens only.
 *
 * Two shape languages: an ENTITY is an opaque rounded card with an icon tile; a
 * RELATIONSHIP is a translucent capsule riding its line, set in mono caps. Aggregated
 * links draw thick, facts thin, switched-off dashed and faded, a new kind for the case
 * in warning colour.
 *
 * Nodes are draggable and their positions are state: dagre only seeds them, and a
 * re-seed happens solely when the structure changes (an entity appears/disappears or
 * grows). Count changes and selection never move a node the investigator placed.
 *
 * On a phone the drawing runs top to bottom: left to right, even two cards side by side
 * are wider than the screen.
 */

import Dagre from '@dagrejs/dagre'
import {
  BaseEdge,
  type Edge,
  EdgeLabelRenderer,
  type EdgeProps,
  type FitViewOptions,
  getSmoothStepPath,
  Handle,
  type Node,
  type NodeProps,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useNodesState,
  useReactFlow,
} from '@xyflow/react'
import { Circle, Layers, Maximize2, Minus, Plus } from 'lucide-react'
import { memo, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ENTITY_ICON_COMPONENTS } from '@/components/neo4j/cytoscape/entity-icons'
import { Button } from '@/components/ui/button'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/utils/cn'
import { entityLabelColor } from '@/utils/entity-colors'
import type { RecipeGraphData, RecipeGraphEdge, RecipeGraphNode } from './recipe-graph-data'
import '@xyflow/react/dist/style.css'

export interface RecipeGraphProps {
  data: RecipeGraphData
  /** Node or edge id shown as selected (ring). */
  selectedId?: string | null
  onSelect?: (id: string | null) => void
  /** Height class of the canvas, e.g. `h-72`. */
  className?: string
}

type RecipeNodeType = Node<
  { node: RecipeGraphNode; selected: boolean; onSelect?: RecipeGraphProps['onSelect'] },
  'recipe'
>
/** Which of the links between the same two cards this one is, and how many there are. */
interface Lane {
  index: number
  count: number
}
type RecipeEdgeType = Edge<
  {
    edge: RecipeGraphEdge
    selected: boolean
    onSelect?: RecipeGraphProps['onSelect']
    lane?: Lane
  },
  'recipe'
>

const NODE_WIDTH = 232
const NODE_HEIGHT = 64
const CELL_ROW = 16

function nodeHeight(node: RecipeGraphNode): number {
  return NODE_HEIGHT + Math.min(node.cells?.length ?? 0, 3) * CELL_ROW
}

/** What a node's placement depends on — positions re-seed only when this changes. */
function signatureOf(data: RecipeGraphData): string {
  return (
    data.nodes.map(n => `${n.id}:${nodeHeight(n)}`).join('|') +
    '#' +
    data.edges.map(e => `${e.source}>${e.target}`).join('|')
  )
}

type Direction = 'LR' | 'TB'

// wide ranks so the label capsules sit on their own stretch of edge, never on a card
const SPACING: Record<Direction, { nodesep: number; ranksep: number }> = {
  LR: { nodesep: 56, ranksep: 250 },
  TB: { nodesep: 20, ranksep: 76 },
}

/** Top to bottom, the strip under the floating zoom buttons stays clear of cards. */
const FIT: Record<Direction, FitViewOptions> = {
  LR: { padding: 0.15, maxZoom: 1 },
  TB: { padding: { top: '52px', x: '8px', bottom: '8px' }, maxZoom: 1 },
}

/** Height a capsule takes on the line, with a little air (drawing units). */
const CAPSULE_SLOT = 27

/** Links that join the same two cards run along one line and would stack their capsules. */
function lanesOf(edges: RecipeGraphEdge[]): Map<string, Lane> {
  const groups = new Map<string, string[]>()
  for (const e of edges) {
    const key = `${e.source}>${e.target}`
    groups.set(key, [...(groups.get(key) ?? []), e.id])
  }
  const lanes = new Map<string, Lane>()
  for (const ids of groups.values()) {
    ids.forEach((id, index) => lanes.set(id, { index, count: ids.length }))
  }
  return lanes
}

/**
 * Deterministic seed layout: subjects and sources first (left, or top on a phone),
 * targets after them.
 */
function layout(data: RecipeGraphData, direction: Direction): RecipeNodeType[] {
  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}))
  g.setGraph({ rankdir: direction, ...SPACING[direction], marginx: 8, marginy: 8 })
  for (const n of data.nodes) g.setNode(n.id, { width: NODE_WIDTH, height: nodeHeight(n) })
  for (const e of data.edges) g.setEdge(e.source, e.target)
  Dagre.layout(g)
  return data.nodes.map(n => {
    const pos = g.node(n.id)
    return {
      id: n.id,
      type: 'recipe' as const,
      position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - nodeHeight(n) / 2 },
      data: { node: n, selected: false },
      sourcePosition: direction === 'TB' ? Position.Bottom : Position.Right,
      targetPosition: direction === 'TB' ? Position.Top : Position.Left,
      connectable: false,
      width: NODE_WIDTH,
      height: nodeHeight(n),
    }
  })
}

const RecipeNode = memo(function RecipeNode({
  data,
  sourcePosition = Position.Right,
  targetPosition = Position.Left,
}: NodeProps<RecipeNodeType>) {
  const { t } = useTranslation()
  const { node, selected } = data
  const Icon = ENTITY_ICON_COMPONENTS[node.label] ?? Circle
  return (
    <div
      className={cn(
        'relative cursor-grab rounded-xl border bg-card px-3 py-2 text-card-foreground shadow-md transition-opacity active:cursor-grabbing',
        'w-[232px]',
        node.state === 'off' && 'opacity-40 border-dashed',
        node.state === 'missing' && 'opacity-70 border-dashed',
        node.state === 'new' && 'border-warning-border',
        selected && 'ring-2 ring-primary'
      )}
    >
      <Handle type="target" position={targetPosition} className="!size-1.5 !bg-border" />
      <div className="flex items-start gap-2">
        <span
          className={cn(
            'mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md',
            entityLabelColor(node.label)
          )}
        >
          <Icon className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <button
              type="button"
              className="nodrag nopan min-w-0 flex-1 truncate text-left text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              title={node.title}
              aria-label={t('tableMapping.graph.inspectEntity', { name: node.title })}
              aria-pressed={selected}
              onClick={event => {
                event.stopPropagation()
                data.onSelect?.(node.id)
              }}
            >
              {node.title}
            </button>
            {node.badge && (
              <span
                className={cn(
                  'shrink-0 rounded px-1 text-2xs font-medium tabular-nums',
                  node.state === 'missing'
                    ? 'bg-muted text-muted-foreground'
                    : 'bg-primary/10 text-primary'
                )}
              >
                {node.badge}
              </span>
            )}
          </div>
          <div className="truncate text-2xs uppercase tracking-wide text-muted-foreground">
            {node.label}
            {node.subtitle ? (
              <span className="normal-case tracking-normal"> · {node.subtitle}</span>
            ) : (
              ''
            )}
          </div>
          {node.cells && node.cells.length > 0 && (
            <ul className="mt-1 space-y-px font-mono text-2xs text-muted-foreground">
              {node.cells.slice(0, 3).map(c => (
                <li key={c.column} className="truncate" title={`${c.column}: ${c.value}`}>
                  <span className="text-foreground/70">{c.column}</span> {c.value || '∅'}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {node.state === 'new' && (
        <span className="absolute -top-2 right-2 rounded-full bg-warning-surface px-1.5 text-2xs text-warning-foreground">
          {t('tableMapping.graph.newType')}
        </span>
      )}
      <Handle type="source" position={sourcePosition} className="!size-1.5 !bg-border" />
    </div>
  )
})

const RecipeEdge = memo(function RecipeEdge({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
}: EdgeProps<RecipeEdgeType>) {
  const { t } = useTranslation()
  const edge = data?.edge
  const selected = data?.selected ?? false
  const [path, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: 12,
  })
  if (!edge) return null
  // Top to bottom, links fanning out of one card share the same bend: sliding each capsule
  // towards its own target keeps neighbours from covering each other.
  const vertical = targetPosition === Position.Top
  const capsuleX = vertical ? sourceX + (targetX - sourceX) * 0.75 : labelX
  // ...and capsules of links between the same two cards take turns above and below the line.
  const lane = data?.lane
  const capsuleY =
    vertical && lane ? labelY + (lane.index - (lane.count - 1) / 2) * CAPSULE_SLOT : labelY
  const aggregated = edge.kind === 'event_aggregate'
  const faded = edge.state === 'off' || edge.state === 'dim'
  const tone =
    edge.state === 'new' ? 'text-warning' : aggregated ? 'text-primary' : 'text-muted-foreground'
  return (
    <g className={cn(tone, faded && 'opacity-30')}>
      <BaseEdge
        path={path}
        style={{
          stroke: 'currentColor',
          strokeWidth: selected ? (aggregated ? 4 : 2.5) : aggregated ? 3 : 1.5,
          strokeDasharray: edge.state === 'off' ? '6 4' : undefined,
        }}
      />
      {/* arrowhead in the edge's own colour (markers cannot take a token) */}
      <polygon
        points={
          vertical
            ? `${targetX},${targetY} ${targetX - 4.5},${targetY - 9} ${targetX + 4.5},${targetY - 9}`
            : `${targetX},${targetY} ${targetX - 9},${targetY - 4.5} ${targetX - 9},${targetY + 4.5}`
        }
        fill="currentColor"
      />
      <EdgeLabelRenderer>
        <button
          type="button"
          aria-label={t('tableMapping.graph.inspectRelationship', { type: edge.type })}
          aria-pressed={selected}
          onClick={event => {
            event.stopPropagation()
            data?.onSelect?.(edge.id)
          }}
          className={cn(
            'nodrag nopan pointer-events-auto absolute inline-flex max-w-[260px] cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-2xs uppercase tracking-wider backdrop-blur-sm',
            edge.state === 'new'
              ? 'border-warning-border bg-warning-surface text-warning-foreground'
              : aggregated
                ? 'border-primary/40 bg-primary/10 text-primary'
                : 'border-border/60 bg-background/80 text-muted-foreground',
            faded && 'opacity-60',
            selected && 'ring-2 ring-primary'
          )}
          style={{ transform: `translate(-50%, -50%) translate(${capsuleX}px, ${capsuleY}px)` }}
          title={[edge.type, edge.badge, edge.detail].filter(Boolean).join(', ')}
        >
          {aggregated && <Layers className="size-3 shrink-0" />}
          <span className="truncate">{edge.type}</span>
          {edge.badge && (
            <span className="shrink-0 whitespace-nowrap tabular-nums normal-case tracking-normal opacity-80">
              {edge.badge}
            </span>
          )}
        </button>
      </EdgeLabelRenderer>
    </g>
  )
})

const nodeTypes = { recipe: RecipeNode }
const edgeTypes = { recipe: RecipeEdge }

function RecipeGraphCanvas({ data, selectedId = null, onSelect, className }: RecipeGraphProps) {
  const { t } = useTranslation()
  const direction: Direction = useIsMobile() ? 'TB' : 'LR'
  const signature = useMemo(() => signatureOf(data), [data])
  const [nodes, setNodes, onNodesChange] = useNodesState<RecipeNodeType>([])
  const { fitView } = useReactFlow()
  const seeded = useRef<{ signature: string; direction: Direction } | null>(null)

  // Re-seed positions only when the structure changed; otherwise patch content in place
  // so a dragged node stays exactly where the investigator put it.
  useEffect(() => {
    const last = seeded.current
    if (last?.signature === signature && last.direction === direction) {
      const fresh = new Map(data.nodes.map(n => [n.id, n]))
      setNodes(current =>
        current.map(n => {
          const node = fresh.get(n.id)
          return node ? { ...n, data: { ...n.data, node } } : n
        })
      )
      return
    }
    // a turned drawing is laid out afresh: positions kept from the other direction mean nothing
    const turned = !!last && last.direction !== direction
    seeded.current = { signature, direction }
    const laid = layout(data, direction)
    setNodes(current => {
      const kept = new Map(current.map(n => [n.id, n.position]))
      if (turned) kept.clear()
      return laid.map(n => {
        const position = kept.get(n.id)
        return position ? { ...n, position } : n
      })
    })
    requestAnimationFrame(() => fitView(FIT[direction]))
  }, [signature, data, direction, setNodes, fitView])

  // selection is injected at render time so it can never touch positions
  const renderedNodes = useMemo(
    () => nodes.map(n => ({ ...n, data: { ...n.data, selected: n.id === selectedId, onSelect } })),
    [nodes, selectedId, onSelect]
  )
  const lanes = useMemo(() => lanesOf(data.edges), [data.edges])
  const renderedEdges = useMemo<RecipeEdgeType[]>(
    () =>
      data.edges.map(e => ({
        id: e.id,
        source: e.source,
        target: e.target,
        type: 'recipe' as const,
        data: { edge: e, selected: e.id === selectedId, onSelect, lane: lanes.get(e.id) },
      })),
    [data.edges, lanes, selectedId, onSelect]
  )

  if (data.nodes.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-xl border border-dashed text-xs text-muted-foreground',
          className ?? 'h-72'
        )}
      >
        {t('tableMapping.graph.empty')}
      </div>
    )
  }
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl border border-border/60 bg-background/60',
        className ?? 'h-72'
      )}
    >
      <ReactFlow
        nodes={renderedNodes}
        edges={renderedEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        minZoom={0.3}
        maxZoom={1.4}
        proOptions={{ hideAttribution: true }}
        nodesDraggable
        nodeDragThreshold={4}
        nodesConnectable={false}
        elementsSelectable={false}
        zoomOnScroll={false}
        zoomOnDoubleClick={false}
        preventScrolling={false}
        panOnDrag
        onNodeClick={(_event, node) => onSelect?.(node.id)}
        onEdgeClick={(_event, edge) => onSelect?.(edge.id)}
        onPaneClick={() => onSelect?.(null)}
      />
      <GraphControls fit={FIT[direction]} />
    </div>
  )
}

function GraphControls({ fit }: { fit: FitViewOptions }) {
  const { t } = useTranslation()
  const { zoomIn, zoomOut, fitView } = useReactFlow()
  return (
    <div className="absolute right-2 top-2 z-10 flex gap-0.5 rounded-md border bg-card p-0.5">
      <Button
        type="button"
        size="icon-xs"
        variant="ghost"
        className="max-md:size-9"
        aria-label={t('tableMapping.graph.zoomIn')}
        onClick={() => void zoomIn()}
      >
        <Plus className="size-3.5" />
      </Button>
      <Button
        type="button"
        size="icon-xs"
        variant="ghost"
        className="max-md:size-9"
        aria-label={t('tableMapping.graph.zoomOut')}
        onClick={() => void zoomOut()}
      >
        <Minus className="size-3.5" />
      </Button>
      <Button
        type="button"
        size="icon-xs"
        variant="ghost"
        className="max-md:size-9"
        aria-label={t('tableMapping.graph.fit')}
        onClick={() => void fitView(fit)}
      >
        <Maximize2 className="size-3.5" />
      </Button>
    </div>
  )
}

export function RecipeGraph(props: RecipeGraphProps) {
  return (
    <ReactFlowProvider>
      <RecipeGraphCanvas {...props} />
    </ReactFlowProvider>
  )
}
